import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { focusPanelItems, FocusPanelContext, FocusPanelItemConfig } from './focusPanelConfig';

/*
 * useFocusPanelTodos.ts
 * Fetches the live, not period bound data behind the focus panel
 * todos, shared by the static focus panel and the todos widget.
 */

// Fetches the todos context and derives the rows which should be shown
export const useFocusPanelTodos = () => {
    const history = useHistory();
    const { allowedFeatures, userHash, handleLogout } = useAuthContext();
    const [context, setContext] = useState<FocusPanelContext | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch the live focus panel data, not bound to the dashboard period
    useEffect(() => {
        const todayIso = new Date().toISOString().slice(0, 10);

        const fetchFocusPanelData = async () => {
            setLoading(true);

            // Fetch the current statuses of every allowed feature, plus the current users due tasks
            const [job, proposal, purchase, invoice, taskIds] = await Promise.all([
                allowedFeatures.includes('jobs')
                    ? fetchData({ apiUrl: `get_current_job_statuses/?from=${todayIso}&to=${todayIso}`, handleLogout })
                    : Promise.resolve([]),
                allowedFeatures.includes('proposals')
                    ? fetchData({ apiUrl: `get_current_proposal_statuses/?from=${todayIso}&to=${todayIso}`, handleLogout })
                    : Promise.resolve([]),
                allowedFeatures.includes('purchases')
                    ? fetchData({ apiUrl: `get_current_purchase_statuses/?from=${todayIso}&to=${todayIso}`, handleLogout })
                    : Promise.resolve([]),
                allowedFeatures.includes('invoices')
                    ? fetchData({ apiUrl: `get_current_invoice_statuses/?from=${todayIso}&to=${todayIso}`, handleLogout })
                    : Promise.resolve([]),
                fetchData({ apiUrl: 'get_current_user_tasks', handleLogout }),
            ]);

            // Save the combined focus panel context in the state
            setContext({
                job: job || [],
                proposal: proposal || [],
                purchase: purchase || [],
                invoice: invoice || [],
                taskCount: (taskIds || []).length,
                userHash,
                todayIso,
            });

            // Only stop loading once the data has actually arrived
            setLoading(false);
        };

        fetchFocusPanelData();
    }, [allowedFeatures, userHash]);

    // Navigate to the list view of a row, filtered on its matching params
    const navigateToItem = (item: FocusPanelItemConfig) => {
        if (!context) {
            return;
        }

        const params = item.params(context);
        const queryString = Object.entries(params).map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');

        history.push(`${item.route}?${queryString}`);
    };

    // Only keep the rows whose feature is allowed and which actually have a count
    const visibleRows = context
        ? focusPanelItems
            .filter(item => !item.requiredFeature || allowedFeatures.includes(item.requiredFeature))
            .map(item => ({ item, count: item.count(context) }))
            .filter(({ count }) => count > 0)
        : [];

    return { loading, visibleRows, navigateToItem };
};
